// Phase 3 — Per-category pricing mode + caps editor.
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingCategoryRules,
  useUpsertCategoryRule,
  useDeleteCategoryRule,
  type ShopPricingCategoryRuleRow,
} from '@/hooks/pricing/useShopPricingConfig';
import type { PricingMode } from '@/lib/parts-financial/calculator';

interface Props { shopId: string; }

interface DraftRule {
  id?: string;
  category: string;
  pricing_mode: PricingMode | null;
  markup_percent: number | null;
  optiguard_target_gp: number | null;
  min_markup_pct: number | null;
  max_markup_pct: number | null;
}

const fromRow = (r: ShopPricingCategoryRuleRow): DraftRule => ({
  id: r.id,
  category: r.category,
  pricing_mode: r.pricing_mode,
  markup_percent: r.markup_percent,
  optiguard_target_gp: r.optiguard_target_gp,
  min_markup_pct: r.min_markup_pct,
  max_markup_pct: r.max_markup_pct,
});

export function CategoryRulesTable({ shopId }: Props) {
  const rulesQ = useShopPricingCategoryRules(shopId);
  const upsert = useUpsertCategoryRule();
  const del = useDeleteCategoryRule();

  const [draft, setDraft] = useState<DraftRule | null>(null);

  const startNew = () => setDraft({
    category: '', pricing_mode: null, markup_percent: null,
    optiguard_target_gp: null, min_markup_pct: null, max_markup_pct: null,
  });

  const saveDraft = async () => {
    if (!draft || !draft.category.trim()) {
      toast({ title: 'Category name required', variant: 'destructive' });
      return;
    }
    try {
      await upsert.mutateAsync({
        shop_id: shopId,
        category: draft.category.trim(),
        pricing_mode: draft.pricing_mode,
        markup_percent: draft.markup_percent,
        optiguard_target_gp: draft.optiguard_target_gp,
        min_markup_pct: draft.min_markup_pct,
        max_markup_pct: draft.max_markup_pct,
        enabled: true,
      });
      toast({ title: 'Category rule saved' });
      setDraft(null);
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  const updateExisting = async (r: DraftRule) => {
    try {
      await upsert.mutateAsync({
        id: r.id, shop_id: shopId, category: r.category,
        pricing_mode: r.pricing_mode, markup_percent: r.markup_percent,
        optiguard_target_gp: r.optiguard_target_gp,
        min_markup_pct: r.min_markup_pct, max_markup_pct: r.max_markup_pct,
        enabled: true,
      });
      toast({ title: 'Updated' });
    } catch (e: any) {
      toast({ title: 'Update failed', description: e?.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await del.mutateAsync({ id, shopId });
      toast({ title: 'Removed' });
    } catch (e: any) {
      toast({ title: 'Remove failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Category Overrides</CardTitle>
            <CardDescription>
              Override the default mode per part category. Caps are applied after the suggestion is computed.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={startNew}>
            <Plus className="h-4 w-4 mr-1" /> Add category
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
          <div className="col-span-3">Category</div>
          <div className="col-span-2">Mode</div>
          <div className="col-span-1 text-right">Markup %</div>
          <div className="col-span-1 text-right">Target GP %</div>
          <div className="col-span-2 text-right">Min / Max markup</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {(rulesQ.data ?? []).map((r) => (
          <RuleRow
            key={r.id}
            initial={fromRow(r)}
            onSave={updateExisting}
            onDelete={() => handleDelete(r.id)}
          />
        ))}

        {draft && (
          <RuleRow
            initial={draft}
            isNew
            onSave={() => saveDraft()}
            onChange={setDraft}
            onCancel={() => setDraft(null)}
          />
        )}

        {!draft && !(rulesQ.data ?? []).length && (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md">
            No category overrides yet. Default mode applies to all categories.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface RuleRowProps {
  initial: DraftRule;
  isNew?: boolean;
  onSave: (r: DraftRule) => void | Promise<void>;
  onDelete?: () => void;
  onCancel?: () => void;
  onChange?: (r: DraftRule) => void;
}

function RuleRow({ initial, isNew, onSave, onDelete, onCancel, onChange }: RuleRowProps) {
  const [r, setR] = useState<DraftRule>(initial);

  const update = (patch: Partial<DraftRule>) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange?.(next);
  };

  const num = (v: string): number | null => (v === '' ? null : Number(v));

  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-muted/30 rounded-md p-2">
      <div className="col-span-3">
        <Input
          value={r.category}
          onChange={(e) => update({ category: e.target.value })}
          placeholder="e.g. Brakes"
          disabled={!isNew}
        />
      </div>
      <div className="col-span-2">
        <Select
          value={r.pricing_mode ?? 'inherit'}
          onValueChange={(v) => update({ pricing_mode: v === 'inherit' ? null : (v as PricingMode) })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">Inherit default</SelectItem>
            <SelectItem value="flat">Flat</SelectItem>
            <SelectItem value="matrix">Matrix</SelectItem>
            <SelectItem value="optiguard">OptiGuard</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1">
        <Input
          type="number" step="0.1"
          value={r.markup_percent ?? ''}
          onChange={(e) => update({ markup_percent: num(e.target.value) })}
          placeholder="—"
          className="text-right"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number" step="0.1"
          value={r.optiguard_target_gp ?? ''}
          onChange={(e) => update({ optiguard_target_gp: num(e.target.value) })}
          placeholder="—"
          className="text-right"
        />
      </div>
      <div className="col-span-2 flex gap-1">
        <Input
          type="number" step="1"
          value={r.min_markup_pct ?? ''}
          onChange={(e) => update({ min_markup_pct: num(e.target.value) })}
          placeholder="min"
          className="text-right"
        />
        <Input
          type="number" step="1"
          value={r.max_markup_pct ?? ''}
          onChange={(e) => update({ max_markup_pct: num(e.target.value) })}
          placeholder="max"
          className="text-right"
        />
      </div>
      <div className="col-span-3 flex justify-end gap-1">
        <Button size="sm" variant="default" onClick={() => onSave(r)}>
          <Save className="h-3.5 w-3.5 mr-1" /> {isNew ? 'Add' : 'Save'}
        </Button>
        {isNew && onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        )}
        {!isNew && onDelete && (
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
